import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, ClipboardCopy, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { getStoredErrors, storeErrors, type ClientError } from '@/lib/error-store';

export function ErrorLogTab() {
  const [errors, setErrors] = useState<ClientError[]>(getStoredErrors);

  useEffect(() => {
    const refresh = () => setErrors(getStoredErrors());
    window.addEventListener('pm-error-update', refresh);
    const interval = setInterval(refresh, 2000);
    return () => { window.removeEventListener('pm-error-update', refresh); clearInterval(interval); };
  }, []);

  const copyAll = () => {
    const text = errors.map((e) => `[${e.timestamp}] ${e.source || 'unknown'}\n${e.message}\n${e.stack || ''}\n`).join('\n---\n');
    navigator.clipboard.writeText(text).then(() => toast.success('Errors copied to clipboard'));
  };

  const clearAll = () => {
    storeErrors([]);
    setErrors([]);
    toast.success('Error log cleared');
  };

  return (
    <Card className="overflow-hidden rounded-3xl border border-border bg-card shadow-glass">
      <CardHeader className="flex flex-col items-start justify-between gap-4 border-b bg-muted/30 p-8 sm:flex-row sm:items-center">
        <div>
          <CardTitle className="flex items-center gap-3 text-foreground font-black uppercase tracking-tight">
            <AlertTriangle className="h-5 w-5 text-destructive" />Client Error Log
          </CardTitle>
          <CardDescription className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            Runtime errors captured from this browser session. Copy and address as needed.
          </CardDescription>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={copyAll} disabled={!errors.length} className="h-10 rounded-xl border-border font-black uppercase text-muted-foreground">
            <ClipboardCopy className="mr-2 h-4 w-4" />Copy All
          </Button>
          <Button variant="outline" onClick={clearAll} disabled={!errors.length} className="h-10 rounded-xl border-destructive/50 font-black uppercase text-destructive hover:bg-destructive/10">
            <Trash2 className="mr-2 h-4 w-4" />Clear
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {errors.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/10">
              <AlertTriangle className="h-8 w-8 text-emerald-500" />
            </div>
            <p className="font-black uppercase tracking-tight text-foreground">No Errors Captured</p>
            <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Runtime errors will appear here automatically.</p>
          </div>
        ) : (
          <div className="max-h-[600px] divide-y divide-border overflow-y-auto">
            {errors.slice().reverse().map((err) => (
              <div key={err.id} className="p-5 hover:bg-muted/20">
                <div className="mb-2 flex items-center gap-3">
                  <Badge className={`border-none text-[9px] font-black uppercase ${
                    err.level === 'warning' ? 'bg-amber-500/10 text-amber-500' : 'bg-destructive/10 text-destructive'
                  }`}>{err.level || 'Error'}</Badge>
                  {err.category && <Badge variant="outline" className="text-[9px] font-bold uppercase border-border text-muted-foreground">{err.category}</Badge>}
                  <span className="text-[10px] font-bold text-muted-foreground">{new Date(err.timestamp).toLocaleString()}</span>
                  {err.source && <span className="text-[10px] font-mono text-muted-foreground/70">{err.source}</span>}
                </div>
                <p className="font-bold text-foreground">{err.message}</p>
                {err.stack && (
                  <pre className="mt-2 max-h-32 overflow-auto rounded-xl bg-muted/50 p-3 text-[11px] font-mono text-muted-foreground">{err.stack}</pre>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
